import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Wallet, Users, ArrowRight, Copy } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showFamilyCode, setShowFamilyCode] = useState(false);
  const [newFamilyCode, setNewFamilyCode] = useState('');
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [familyCode, setFamilyCode] = useState('');
  const [joinFamily, setJoinFamily] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Benvenuto!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(
        registerEmail, 
        registerPassword, 
        registerName, 
        joinFamily ? familyCode : null
      );
      if (!joinFamily) {
        setNewFamilyCode(result.family_code);
        setShowFamilyCode(true);
      } else {
        toast.success('Registrazione completata!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const copyFamilyCode = () => {
    navigator.clipboard.writeText(newFamilyCode);
    toast.success('Codice copiato!');
  };

  const continueAfterRegister = () => {
    toast.success('Registrazione completata!');
    navigate('/');
  };

  if (showFamilyCode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md animate-in">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Famiglia Creata!</CardTitle>
            <CardDescription>
              Condividi questo codice con i membri della tua famiglia per permettere loro di unirsi
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-4 flex items-center justify-between">
              <span className="font-mono text-2xl font-bold tracking-wider">{newFamilyCode}</span>
              <Button variant="ghost" size="icon" onClick={copyFamilyCode} data-testid="copy-family-code-btn">
                <Copy className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Il secondo membro della famiglia dovrà inserire questo codice durante la registrazione
            </p>
            <Button 
              className="w-full" 
              onClick={continueAfterRegister}
              data-testid="continue-after-register-btn"
            >
              Continua <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8 animate-in">
        {/* Logo/Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <Wallet className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Budget Pro</h1>
          <p className="text-muted-foreground mt-2">Gestisci le finanze della tua famiglia</p>
        </div>

        {/* Auth Card */}
        <Card>
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrati</TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin}>
                <CardHeader>
                  <CardTitle>Bentornato!</CardTitle>
                  <CardDescription>Inserisci le tue credenziali per accedere</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="nome@esempio.it"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Accesso in corso...' : 'Accedi'}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>
            
            {/* Register Tab */}
            <TabsContent value="register">
              <form onSubmit={handleRegister}>
                <CardHeader>
                  <CardTitle>Crea un account</CardTitle>
                  <CardDescription>Inizia a gestire il budget familiare</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Nome</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="Il tuo nome"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="nome@esempio.it"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                  </div>
                  
                  {/* Join Family Option */}
                  <div className="pt-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="join-family"
                        checked={joinFamily}
                        onChange={(e) => setJoinFamily(e.target.checked)}
                        className="rounded border-input"
                        data-testid="join-family-checkbox"
                      />
                      <Label htmlFor="join-family" className="text-sm cursor-pointer">
                        Ho un codice famiglia esistente
                      </Label>
                    </div>
                    
                    {joinFamily && (
                      <div className="mt-3">
                        <Input
                          type="text"
                          placeholder="Inserisci codice famiglia"
                          value={familyCode}
                          onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
                          className="font-mono uppercase"
                          required={joinFamily}
                          data-testid="family-code-input"
                        />
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Registrazione...' : (joinFamily ? 'Unisciti alla famiglia' : 'Crea famiglia')}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
